import React, { memo, useMemo } from "react";

import VideoWrapper from "@Components/activity/VideoWrapper";
import { IActivityDetails } from "@Models/IActivityDetails";
import { hasCapability, prettifyName, useSearchParams } from "@Utils";
import { Alert, Col, Row, Tag } from "antd";
import { SystemContext } from "@Constants";
import ActivityCopy from "@Components/activity/ActivityCopy";
import LargeSpinner from "@Components/shared/LargeSpinner";
import useQuestionnaireInstance from "@Components/questionnaires/hooks/useQuestionnaireInstance";
import useAssociatedStatuses from "./hooks/useAssociatedStatuses";
import SessionManager from "@Core/session";

interface IProps {
    activity: IActivityDetails;
    canRender: boolean;
}

const PublicView: React.FC<IProps> = ({ activity, canRender }: IProps) => {
    const hasVideo = useMemo(() => ![null, ""].includes(activity?.mediaUrl), [activity?.mediaUrl]);
    const canCopy = hasCapability("Activity/GetUser", SystemContext);
    const { userId: loggedInUserId } = SessionManager.getServiceUser();
    const [searchParams] = useSearchParams();
    const urlParamActivityId = searchParams.get("activity");

    const {
        onAnswerQuestion,
        onComplete,
        onGetStarted,
        isLoadingCreateQuestionnaire,
        isFetching,
        questionnaireInstance,
    } = useQuestionnaireInstance({
        activityCategoryId: activity?.activityCategoryId,
        activityId: activity?.activityId,
        activityInstanceId: activity?.activityInstanceId,
    });

    const hasMeta = () => {
        return (
            activity?.activityMeta !== null &&
            activity?.activityMeta?.description !== null &&
            (activity.activityMeta?.description !== "" || activity.activityMeta?.tags?.length > 0)
        );
    };

    const { associatedStatusesMap, isHydrated } = useAssociatedStatuses({
        activities: activity ? [activity] : [],
        loggedInUser: loggedInUserId,
    });

    const associatedStatus = associatedStatusesMap.get(urlParamActivityId);
    /**
     * Render a video feature for the current activity.
     */
    const renderVideo = (activity: IActivityDetails) => {
        return (
            activity !== undefined &&
            hasVideo && (
                <VideoWrapper
                    activity={activity}
                    isFetching={isFetching}
                    hasVideo={hasVideo}
                    isLoadingCreateQuestionnaire={isLoadingCreateQuestionnaire}
                    onAnswerQuestion={onAnswerQuestion}
                    onComplete={onComplete}
                    onGetStarted={onGetStarted}
                    questionnaireInstance={questionnaireInstance}
                    associatedStatus={associatedStatus}
                    isHydrated={isHydrated}
                />
            )
        );
    };

    const getTags = () => {
        return activity.activityMeta?.tags?.map((x) => (
            <Tag key={x.toString()} color={"cyan"}>
                {prettifyName(x.toString())}
            </Tag>
        ));
    };

    return (
        <section>
            <Row>
                <Col md={24} lg={24} xl={24}>
                    {canRender && hasMeta() ? (
                        <Alert
                            style={{ margin: "0px 20px 6px 10px" }}
                            description={
                                <>
                                    {activity.activityMeta?.description}
                                    <span style={{ paddingLeft: "20px" }}>{getTags()}</span>
                                </>
                            }
                            type="info"
                            showIcon
                        />
                    ) : null}
                    {canRender ? (
                        <>
                            {renderVideo(activity)}
                            {hasVideo && canCopy ? (
                                <div style={{ marginTop: "20px" }}>
                                    <ActivityCopy activity={activity} />
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <LargeSpinner />
                    )}
                </Col>
            </Row>
        </section>
    );
};

export default memo(PublicView);
